<template>
    <component
        v-if="isRoutable && !collapsible"
        :is="componentType"
        v-bind="routingProps"
        v-slot="{ href, route, navigate, isActive, isExactActive }"
        custom
    >
        <li :class="[$style['mds-list-group-item'], classObject, activeClass(isActive, isExactActive)]">
            <a :href="href" :tabIndex="tabIndex" :class="$style['mds-list-group__link']" @click="navigate">
                <div :class="$style['mds-list-group-item__link-inner']">
                    <div :class="[$style['mds-list-group-item__before'], indentClass]">
                        <div v-if="collapsible" :class="$style['mds-list-group-item__icon-container']">
                            <mds-icon
                                name="triangle-fill-right"
                                title="triangle-fill-right"
                                :class="[$style['mds-list-group__icon--toggle'], $style['mds-list-group__icon']]"
                                :size="iconSize"
                            />
                        </div>
                        <div v-if="icon" :class="$style['mds-list-group-item__icon-container']">
                            <mds-icon
                                :name="icon"
                                :title="icon"
                                :class="$style['mds-list-group__icon']"
                                :size="iconSize"
                            ></mds-icon>
                        </div>
                        <img
                            v-if="image"
                            :class="[$style['mds-list-group-item__image'], imageRoundClass]"
                            :src="image"
                            aria-hidden="true"
                            alt=""
                        />
                    </div>
                    <div :class="$style['mds-list-group-item__main']">
                        <div :class="$style['mds-list-group-item__upper']">
                            <span :class="$style['mds-list-group-item__text']">
                                <slot>{{ text }}</slot>
                                <span
                                    v-if="metadata"
                                    :class="$style['mds-list-group-item__metadata']"
                                    v-html="metadata"
                                ></span>
                            </span>
                            <div v-if="hasRightSlot" :class="$style['mds-list-group-item__right-slot']">
                                <slot name="mds-list-item-right" />
                            </div>
                        </div>
                        <div v-if="hasLowerContent" :class="$style['mds-list-group-item__lower']">
                            <div v-if="hasMicrocopySlot || microcopy" :class="$style['mds-list-group-item__microcopy']">
                                <slot name="mds-list-item-microcopy">
                                    <span v-if="microcopy" v-html="microcopy"></span>
                                </slot>
                            </div>
                            <div
                                v-if="metadataBelow && metadataBelow.length"
                                :class="$style['mds-list-group-item__metadata-below']"
                            >
                                <span
                                    v-for="(item, index) in metadataBelow"
                                    :key="`metabelow-${index}`"
                                    v-html="item"
                                    :class="$style['mds-list-group-item__metadata-below-item']"
                                ></span>
                            </div>
                        </div>
                    </div>
                </div>
            </a>

            <mds-list-group
                v-if="hasChildrenSlot && (expanded || !collapsible)"
                :id="ariaControls"
                :size="parentVariation.size"
                :class="$style['mds-list-group--child']"
            >
                <slot name="mds-list-item-children" />
            </mds-list-group>
        </li>
    </component>

    <li v-else :class="[$style['mds-list-group-item'], classObject]">
        <component
            :is="componentType"
            :href="href"
            :class="$style['mds-list-group__link']"
            :aria-disabled="disabled"
            :tabindex="tabIndex"
            :aria-expanded="ariaExpanded"
            :aria-controls="ariaControls"
            :role="role"
        >
            <div :class="$style['mds-list-group-item__link-inner']">
                <div :class="[$style['mds-list-group-item__before'], indentClass]">
                    <div v-if="collapsible" :class="$style['mds-list-group-item__icon-container']">
                        <mds-icon
                            name="triangle-fill-right"
                            title="triangle-fill-right"
                            :class="[$style['mds-list-group__icon--toggle'], $style['mds-list-group__icon']]"
                            :size="iconSize"
                        />
                    </div>
                    <div v-if="icon" :class="$style['mds-list-group-item__icon-container']">
                        <mds-icon
                            :name="icon"
                            :title="icon"
                            :class="$style['mds-list-group__icon']"
                            :size="iconSize"
                        ></mds-icon>
                    </div>
                    <img
                        v-if="image"
                        :class="[$style['mds-list-group-item__image'], imageRoundClass]"
                        :src="image"
                        aria-hidden="true"
                        alt=""
                    />
                </div>
                <div :class="$style['mds-list-group-item__main']">
                    <div :class="$style['mds-list-group-item__upper']">
                        <span :class="$style['mds-list-group-item__text']">
                            <slot>{{ text }}</slot>
                            <span
                                v-if="metadata"
                                :class="$style['mds-list-group-item__metadata']"
                                v-html="metadata"
                            ></span>
                        </span>
                        <div v-if="hasRightSlot" :class="$style['mds-list-group-item__right-slot']">
                            <slot name="mds-list-item-right" />
                        </div>
                    </div>
                    <div v-if="hasLowerContent" :class="$style['mds-list-group-item__lower']">
                        <div v-if="hasMicrocopySlot || microcopy" :class="$style['mds-list-group-item__microcopy']">
                            <slot name="mds-list-item-microcopy">
                                <span v-if="microcopy" v-html="microcopy"></span>
                            </slot>
                        </div>
                        <div
                            v-if="metadataBelow && metadataBelow.length"
                            :class="$style['mds-list-group-item__metadata-below']"
                        >
                            <span
                                v-for="(item, index) in metadataBelow"
                                :key="`metabelow-${index}`"
                                v-html="item"
                                :class="$style['mds-list-group-item__metadata-below-item']"
                            ></span>
                        </div>
                    </div>
                </div>
            </div>
        </component>

        <mds-list-group
            v-if="hasChildrenSlot && (expanded || !collapsible)"
            :id="ariaControls"
            :size="parentVariation.size"
            :class="$style['mds-list-group--child']"
        >
            <slot name="mds-list-item-children" />
        </mds-list-group>
    </li>
</template>

<script>
    import MdsIcon from '@mds/icon';
    import MdsRoutable from '@mds/routable';
    import MdsListGroup from './list-group.vue';
    import { MdsUniqueId } from '@mds/utils-js';

    export default {
        name: 'MdsListGroupItem',
        components: {
            MdsIcon,
            MdsListGroup,
        },
        mixins: [MdsRoutable, MdsUniqueId],
        inject: ['parentVariation'], // Properties that are provided via ancestor components https://vuejs.org/v2/api/#provide-inject
        props: {
            active: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, sets the item to active.',
                },
            },
            collapsible: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'If `true`, enables the ability to collapse a group and disables the linking functionality.',
                },
            },
            disabled: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, sets the item to disabled.',
                },
            },
            expanded: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, expands a collapsible section.',
                },
            },
            href: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'When provided, makes the list group item `a`-based and sets the `href` path.',
                },
            },
            icon: {
                type: String,
                docs: {
                    validation: 'Must be a valid icon name.',
                    description: 'Sets the item icon on the first level.',
                },
            },
            image: {
                type: String,
                docs: {
                    validation: 'Must be a valid icon name.',
                    description: 'Sets the item image.',
                },
            },
            imageRound: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, makes the item image round.',
                },
            },
            metadata: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the item metadata.',
                    support: ['v-html'],
                },
            },
            metadataBelow: {
                type: Array,
                docs: {
                    validation: '—',
                    description: 'Sets the item metadata below.',
                    support: ['v-html'],
                },
            },
            microcopy: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the item microcopy.',
                },
            },
            separatorBefore: {
                type: Boolean,
                default: false,
                docs: {
                    validation: 'Only use within a collapsible group.',
                    description: 'If `true`, adds a separator above the item.',
                },
            },
            text: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the item text.',
                },
            },
        },
        computed: {
            ariaControls() {
                const expandedId = this.generateUuid('mds-list-group-item');
                return this.collapsible ? expandedId : null;
            },
            ariaExpanded() {
                return !this.collapsible ? null : this.expanded ? 'true' : 'false';
            },
            classObject() {
                const obj = {};
                obj[this.$style['mds-list-group-item--disabled']] = this.disabled;
                obj[this.$style['mds-list-group-item--expanded']] = this.expanded;
                obj[this.$style['mds-list-group-item--separator-before']] = this.separatorBefore;
                obj[this.$style['mds-list-group-item--active']] = this.active;
                obj[this.$style['mds-list-group-item--has-collapsible-child']] = this.collapsible;

                return obj;
            },
            componentType() {
                return this.isRoutable ? this.linkTag : this.href ? 'a' : 'span';
            },
            hasRightSlot() {
                return this.$scopedSlots['mds-list-item-right'];
            },
            hasMicrocopySlot() {
                return this.$scopedSlots['mds-list-item-microcopy'];
            },
            hasChildrenSlot() {
                return this.$scopedSlots['mds-list-item-children'];
            },
            hasLowerContent() {
                return this.hasMicrocopySlot || this.microcopy || (this.metadataBelow && this.metadataBelow.length);
            },
            iconSize() {
                return this.parentVariation.size === 'small' || this.parentVariation.size === 'medium'
                    ? 'small'
                    : 'medium';
            },
            imageRoundClass() {
                return this.imageRound ? this.$style['mds-list-group-item__image--round'] : '';
            },
            indentClass() {
                return !this.collapsible ? this.$style['mds-list-group-item__before--indent'] : '';
            },
            role() {
                return this.collapsible ? 'button' : null;
            },
            tabIndex() {
                return this.disabled ? -1 : 0;
            },
        },
        methods: {
            activeClass(isActive, isExactActive) {
                const active = this.exact ? isExactActive : isActive;

                return active ? this.$style['mds-list-group-item--active'] : null;
            },
        },
    };
</script>

<style lang="scss" module>
    @import './list-group.scss';
</style>
